import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CloudUpload, Loader2, Sparkles } from "lucide-react";
import { announceSaved } from "@/components/GuestNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, TmdbAttribution } from "@/components/Brand";

const HERO = "https://images.unsplash.com/photo-1733232073397-80fb13ef3212?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={go} data-testid="auth-google-button"
      className="w-full h-12 rounded-full gap-3 font-semibold">
      <svg viewBox="0 0 48 48" className="h-5 w-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      Continue with Google
    </Button>
  );
}

function AuthForm({ mode }) {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = mode === "signup" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(mode === "signup" ? "/auth/register" : "/auth/login", payload);
      announceSaved(data, !!user?.is_guest);
      setUser(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`auth-form-${mode}`}>
      {mode === "signup" && (
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input id="name" required value={form.name} onChange={set("name")} className="h-12 rounded-xl" data-testid="register-name-input" />
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" required value={form.email} onChange={set("email")} className="h-12 rounded-xl"
          data-testid={mode === "signup" ? "register-email-input" : "login-email-input"} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input id="password" type="password" required minLength={mode === "signup" ? 6 : 1} value={form.password}
          onChange={set("password")} className="h-12 rounded-xl"
          data-testid={mode === "signup" ? "register-password-input" : "login-password-input"} />
      </div>
      {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
      <Button type="submit" disabled={busy} className="w-full h-12 rounded-full font-bold text-base"
        data-testid={mode === "signup" ? "auth-signup-button" : "auth-login-button"}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {mode === "signup" ? "Create account" : "Sign in"}
      </Button>
    </form>
  );
}

function GuestBanner() {
  return (
    <div className="rounded-2xl border border-amber-400/50 bg-amber-400/10 p-4 mb-6 flex gap-3" data-testid="login-guest-banner">
      <CloudUpload className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
      <div>
        <p className="font-bold text-sm">Your progress isn't saved yet.</p>
        <p className="text-xs text-muted-foreground mt-1">
          Right now it lives only in this browser and could be lost. Log in or create an account and everything you've done moves over automatically.
        </p>
      </div>
    </div>
  );
}

function TryButton() {
  const { startGuest } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await startGuest();
      navigate("/onboarding");
    } catch (err) {
      setBusy(false);
      toast.error(formatApiError(err));
    }
  };
  return (
    <Link to="/" onClick={go} aria-disabled={busy} data-testid="login-try-button"
      className="group relative w-full h-14 rounded-full flex items-center justify-center gap-2 font-black text-base text-black bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400 shadow-[0_0_36px_rgba(251,191,36,0.45)] transition-transform hover:scale-[1.03] active:scale-95">
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5 transition-transform group-hover:rotate-12" />} Try it now — no account needed
    </Link>
  );
}

export default function Login() {
  const { user } = useAuth();
  const guest = !!user?.is_guest;
  const [mode, setMode] = useState(guest ? "signup" : "login");
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
        <div className="absolute bottom-0 left-0 p-14 text-white max-w-xl fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Swipe. Taste. Rank.</p>
          <h1 className="text-5xl xl:text-6xl font-black leading-[0.95]">Stop scrolling.<br />Start picking.</h1>
          <p className="mt-6 text-white/70 text-lg">Four swipes teach StreamPick what you love. Your next favorite movie is a flick away.</p>
        </div>
      </div>
      <div className="relative z-10 flex flex-col justify-between p-6 sm:p-12">
        <div className="flex items-center justify-between gap-3">
          <Logo className="text-2xl" />
          {guest && (
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="login-back-link">
              <ArrowLeft className="h-4 w-4" /> Back to swiping
            </Link>
          )}
        </div>
        <div className="w-full max-w-sm mx-auto py-10 fade-up">
          {!guest && (
            <div className="mb-10">
              <TryButton />
              <p className="text-center text-xs text-muted-foreground mt-3">Every feature works right away. Log in later to keep your progress.</p>
            </div>
          )}
          <h2 className="text-3xl font-black tracking-tight" data-testid="login-title">
            {guest ? "Save your progress" : mode === "login" ? "Welcome back" : "Join StreamPick"}
          </h2>
          <p className="text-muted-foreground mt-2 mb-8 text-sm">Your watchlist, built by your thumbs.</p>
          {guest && <GuestBanner />}
          <Tabs value={mode} onValueChange={setMode} className="mb-6">
            <TabsList className="grid grid-cols-2 w-full rounded-full h-11">
              <TabsTrigger value="login" className="rounded-full" data-testid="auth-tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="signup" className="rounded-full" data-testid="auth-tab-signup">Sign up</TabsTrigger>
            </TabsList>
          </Tabs>
          <AuthForm key={mode} mode={mode} />
          <div className="flex items-center gap-3 my-6 text-xs text-muted-foreground uppercase tracking-widest">
            <div className="h-px bg-border flex-1" /> or <div className="h-px bg-border flex-1" />
          </div>
          <GoogleButton />
        </div>
        <TmdbAttribution compact />
      </div>
    </div>
  );
}
